import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { IntlDurationPipe } from '@app/shared/pipes/intl-duration.pipe';
import { TaskTimer } from '@app/shared/types/models/task/task-timer';
import { TranslatePipe } from '@ngx-translate/core';
import { differenceInSeconds } from 'date-fns';
import moment from 'moment';
import { EMPTY, filter, fromEvent, map, merge, switchMap, timer } from 'rxjs';

export enum TaskTimerMode {
  row = 'row',
  column = 'column',
  topBar = 'topBar',
  readonly = 'readonly'
}

@Component({
  selector: 'proffeo-task-timer',
  templateUrl: './task-timer.component.html',
  host: {
    class: 'min-w-0 max-w-full'
  },
  providers: [IntlDurationPipe],
  imports: [NgTemplateOutlet, MatIconModule, TranslatePipe, IntlDurationPipe]
})
export class TaskTimerComponent {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly intlDurationPipe: IntlDurationPipe = inject(IntlDurationPipe);

  private readonly taskTimerSignal: WritableSignal<TaskTimer | undefined> = signal(undefined);
  private _taskTotalTrackedSeconds?: number = undefined;

  protected readonly taskTimerMode: typeof TaskTimerMode = TaskTimerMode;
  protected readonly passingTime: WritableSignal<string | undefined> = signal(undefined);
  protected alreadyUsedTimeSpanValue: string = undefined;

  public readonly showIcon: InputSignal<boolean> = input<boolean>(true);
  public readonly mode: InputSignal<TaskTimerMode> = input<TaskTimerMode>(TaskTimerMode.column);
  public readonly iconSize: InputSignal<'small' | 'large'> = input<'small' | 'large'>('large');
  public readonly estimation: InputSignal<string | undefined> = input<string | undefined>();
  public readonly summaryTime: InputSignal<string | null | undefined> = input<string | null | undefined>();
  public readonly isReadonly: InputSignal<boolean> = input<boolean>(false);
  public readonly timerStarted: OutputEmitterRef<void> = output<void>();
  public readonly timerStopped: OutputEmitterRef<void> = output<void>();

  public constructor() {
    toObservable(this.taskTimerSignal)
      .pipe(
        switchMap(taskTimer => {
          if (!taskTimer?.timerStartedOn) {
            this.passingTime.set(undefined);
            return EMPTY;
          }

          return merge(
            timer(0, 1000),
            fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'))
          ).pipe(map(() => this.formatElapsedSeconds(this.computeElapsedSeconds())));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(formatted => this.passingTime.set(formatted));
  }

  public get taskTimer(): TaskTimer | undefined {
    return this.taskTimerSignal();
  }

  public get taskTotalTrackedSeconds(): number {
    return this._taskTotalTrackedSeconds;
  }

  @Input()
  public set taskTotalTrackedSeconds(value: number) {
    this._taskTotalTrackedSeconds = value;
    if (!this._taskTotalTrackedSeconds) {
      this.alreadyUsedTimeSpanValue = this.intlDurationPipe.transform('PT0H').toString();
    } else {
      const duration = moment.duration(this._taskTotalTrackedSeconds, 'seconds');
      this.alreadyUsedTimeSpanValue = this.intlDurationPipe.transform(duration.toISOString()).toString();
    }
  }

  @Input()
  public set taskTimer(value: TaskTimer | undefined) {
    this.taskTimerSignal.set(value);
  }

  protected startTimer(): void {
    if (!this.taskTimer) {
      return;
    }
    this.timerStarted.emit();
  }

  protected stopTimer(): void {
    if (!this.taskTimer) {
      return;
    }
    this.timerStopped.emit();
  }

  protected toggleTimer(): void {
    if (!this.isReadonly()) {
      if (this.taskTimer) {
        if (this.taskTimer.isStarted) {
          this.stopTimer();
        } else {
          this.startTimer();
        }
      }
    }
  }

  private computeElapsedSeconds(): number {
    const startedOn = this.taskTimerSignal()?.timerStartedOn;
    if (!startedOn) {
      return 0;
    }
    return Math.max(0, differenceInSeconds(new Date(), startedOn));
  }

  private formatElapsedSeconds(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value: number): string => value.toString().padStart(2, '0');

    if (totalSeconds >= 3600) {
      return `${pad(hours)}:${pad(minutes)}`;
    }
    return `${pad(minutes)}:${pad(seconds)}`;
  }
}
